import { useId, useState, type KeyboardEvent } from "react";
import { when } from "../lib/format";
import { labelIndexes, niceTicks, useWidth } from "./scale";
import styles from "./Chart.module.css";

/** Keys of T whose values are numbers — the only fields a series can plot. */
type NumericKey<T> = { [K in keyof T]: T[K] extends number ? K : never }[keyof T] & string;

export interface Series<T> {
  key: NumericKey<T>;
  label: string;
  color: string;
}

interface TimeChartProps<T extends { day: string }> {
  /** One row per day; `day` is a 'YYYY-MM-DD' key. */
  data: T[];
  series: Series<T>[];
  /** area: one series over time. stacked: columns split into series (e.g. token types). */
  kind: "area" | "stacked";
  format: (value: number) => string;
  height?: number;
  ariaLabel: string;
}

const PAD = { top: 12, right: 12, bottom: 26 };
/** ~6.4px per character at the 11px tick size, plus breathing room before the plot. */
const tickWidth = (labels: string[]) => Math.ceil(Math.max(...labels.map((label) => label.length)) * 6.4) + 14;

/**
 * Daily time chart with a crosshair that snaps to the nearest day and one
 * tooltip listing every series. Keyboard: focus the chart, ← → move the day.
 */
export function TimeChart<T extends { day: string }>({ data, series, kind, format, height = 240, ariaLabel }: TimeChartProps<T>) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const [active, setActive] = useState<number | null>(null);
  const clipId = useId();

  const value = (row: T, key: NumericKey<T>) => Number(row[key] ?? 0);
  const totals = data.map((row) => series.reduce((sum, s) => sum + value(row, s.key), 0));
  const max = kind === "stacked" ? Math.max(...totals, 0) : Math.max(...data.map((row) => value(row, series[0]!.key)), 0);
  const ticks = niceTicks(max);
  const top = ticks.at(-1)!;
  const left = tickWidth(ticks.map(format));
  const innerW = Math.max(0, width - left - PAD.right);
  const innerH = height - PAD.top - PAD.bottom;
  const band = innerW / Math.max(data.length, 1);
  const x = (i: number) => left + band * i + band / 2;
  const y = (value: number) => PAD.top + innerH - (value / top) * innerH;
  const labels = labelIndexes(data.length, Math.max(2, Math.floor(innerW / 72)));

  const onPointer = (clientX: number, rect: DOMRect) => {
    const i = Math.floor((clientX - rect.left - left) / band);
    setActive(i >= 0 && i < data.length ? i : null);
  };
  const onKey = (event: KeyboardEvent) => {
    if (event.key === "ArrowRight") setActive((i) => Math.min(data.length - 1, (i ?? -1) + 1));
    else if (event.key === "ArrowLeft") setActive((i) => Math.max(0, (i ?? data.length) - 1));
    else if (event.key === "Escape") setActive(null);
    else return;
    event.preventDefault();
  };

  const barWidth = Math.min(24, Math.max(2, band - 2));
  const s0 = series[0]!;
  const linePath = data.map((row, i) => `${i ? "L" : "M"}${x(i).toFixed(1)},${y(value(row, s0.key)).toFixed(1)}`).join("");
  const areaPath = `${linePath}L${x(data.length - 1).toFixed(1)},${y(0)}L${x(0).toFixed(1)},${y(0)}Z`;

  return (
    <div ref={ref} className={styles.plot} style={{ height }}>
      {width > 0 && (
        <svg
          width={width}
          height={height}
          role="img"
          aria-label={ariaLabel}
          tabIndex={0}
          onKeyDown={onKey}
          onPointerMove={(event) => onPointer(event.clientX, event.currentTarget.getBoundingClientRect())}
          onPointerLeave={() => setActive(null)}
          onBlur={() => setActive(null)}
        >
          <defs>
            <clipPath id={clipId}>
              <rect x={left} y={PAD.top - 4} width={innerW} height={innerH + 4} />
            </clipPath>
          </defs>

          {ticks.map((tick) => (
            <g key={tick}>
              <line x1={left} x2={width - PAD.right} y1={y(tick)} y2={y(tick)} stroke={tick === 0 ? "var(--axis)" : "var(--grid)"} strokeWidth={1} />
              <text x={left - 8} y={y(tick)} dy="0.32em" textAnchor="end" className={styles.tick}>
                {format(tick)}
              </text>
            </g>
          ))}
          {data.map((row, i) =>
            labels.has(i) ? (
              <text key={row.day} x={x(i)} y={height - 8} textAnchor="middle" className={styles.tick}>
                {when.day(row.day)}
              </text>
            ) : null,
          )}

          {kind === "area" ? (
            <g clipPath={`url(#${clipId})`}>
              <path d={areaPath} fill={`color-mix(in srgb, ${s0.color} 10%, transparent)`} />
              <path d={linePath} fill="none" stroke={s0.color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
            </g>
          ) : (
            data.map((row, i) => {
              let base = 0;
              const visible = series.filter((s) => value(row, s.key) > 0);
              return (
                <g key={row.day} opacity={active === null || active === i ? 1 : 0.55}>
                  {visible.map((s, k) => {
                    const y0 = y(base);
                    base += value(row, s.key);
                    const y1 = y(base);
                    // 2px surface gap between stacked segments; only the top segment gets the 4px rounded end.
                    const gap = k > 0 ? 2 : 0;
                    const h = Math.max(0, y0 - y1 - gap);
                    const isTop = k === visible.length - 1;
                    return h > 0 ? (
                      <path key={s.key} d={columnPath(x(i) - barWidth / 2, y1, barWidth, h, isTop ? Math.min(4, h, barWidth / 2) : 0)} fill={s.color} />
                    ) : null;
                  })}
                </g>
              );
            })
          )}

          {active !== null && (
            <g pointerEvents="none">
              <line x1={x(active)} x2={x(active)} y1={PAD.top} y2={PAD.top + innerH} stroke="var(--ink-3)" strokeWidth={1} />
              {kind === "area" && <circle cx={x(active)} cy={y(value(data[active]!, s0.key))} r={4.5} fill={s0.color} stroke="var(--surface)" strokeWidth={2} />}
            </g>
          )}
        </svg>
      )}

      {active !== null && data[active] && (
        <div
          className={styles.tooltip}
          style={{
            left: Math.min(Math.max(x(active), 90), width - 90),
            top: 0,
          }}
          role="status"
        >
          <div className={styles.tooltipDay}>{when.dayLong(data[active]!.day)}</div>
          {(kind === "stacked" ? [...series].reverse() : series).map((s) => (
            <div key={s.key} className={styles.tooltipRow}>
              <span className={styles.lineKey} style={{ background: s.color }} />
              <span className={styles.tooltipLabel}>{s.label}</span>
              <strong>{format(value(data[active]!, s.key))}</strong>
            </div>
          ))}
          {kind === "stacked" && series.length > 1 && (
            <div className={`${styles.tooltipRow} ${styles.tooltipTotal}`}>
              <span className={styles.tooltipLabel}>Total</span>
              <strong>{format(totals[active]!)}</strong>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** A column with a rounded data-end and a square base. */
function columnPath(x: number, y: number, w: number, h: number, r: number): string {
  if (r <= 0) return `M${x},${y}h${w}v${h}h${-w}Z`;
  return `M${x},${y + h}V${y + r}Q${x},${y} ${x + r},${y}H${x + w - r}Q${x + w},${y} ${x + w},${y + r}V${y + h}Z`;
}
